import {
	LinkableEntityType,
	getLinkableEntityTypeLabel,
} from '../enums/linkable-entity-type.enum';

/**
 * Issue-notification "category" for issues that have no linked entity.
 * Stored in issue_notification_role_config.category alongside the
 * LinkableEntityType values.
 */
export const ISSUE_NOTIFICATION_GENERAL_CATEGORY = 'general';

export interface IssueNotificationCategoryOptionModel {
	value: string;
	label: string;
	/** PD-16841 — which issues the category covers, for the settings screen. */
	description: string;
}

/**
 * PD-16841 — one line per category, because a bare "Client" or "Vehicle"
 * heading did not say that the category is the kind of record the issue is
 * linked to. A Record so a new linkable type cannot ship without a line.
 */
const LINKED_ISSUE_DESCRIPTIONS: Record<LinkableEntityType, string> = {
	[LinkableEntityType.CLIENT]: 'Issues linked to a client.',
	[LinkableEntityType.VENDOR]: 'Issues linked to a vendor.',
	[LinkableEntityType.OPPORTUNITY]: 'Issues linked to a sales opportunity.',
	[LinkableEntityType.ORDER]: 'Issues linked to a customer order.',
	[LinkableEntityType.QUOTE]: 'Issues linked to a quote.',
	[LinkableEntityType.INVOICE]: 'Issues linked to an invoice.',
	[LinkableEntityType.ITEM]: 'Issues linked to a product.',
	[LinkableEntityType.ISSUE]: 'Issues linked to another issue.',
	[LinkableEntityType.LICENSE]: 'Issues linked to a license.',
	[LinkableEntityType.SOURCING_REQUEST]: 'Issues linked to a sourcing request.',
	[LinkableEntityType.VEHICLE]:
		'Issues linked to a vehicle, such as a breakdown or a maintenance problem.',
	[LinkableEntityType.MERCHANDISER]:
		'Issues linked to a merchandiser placed at a customer.',
	[LinkableEntityType.SALES_TRIP_STOP]:
		'Issues linked to a planned stop on a sales trip.',
	[LinkableEntityType.CLIENT_VISIT]: 'Issues linked to a logged client visit.',
	[LinkableEntityType.CONTACT]: 'Issues linked to a person.',
};

/**
 * Every issue-notification category surfaced in the Settings admin: each
 * linkable-entity type (Client, Vendor, Vehicle, ...) plus the General
 * catch-all. Driven off LinkableEntityType so it stays complete automatically
 * as new linkable types are added, then sorted alphabetically by label for
 * display. Labels are English; wrap with t() in the UI.
 */
export const ISSUE_NOTIFICATION_CATEGORIES: ReadonlyArray<IssueNotificationCategoryOptionModel> =
	[
		...Object.values(LinkableEntityType).map((type) => ({
			value: type,
			label: getLinkableEntityTypeLabel(type),
			description: LINKED_ISSUE_DESCRIPTIONS[type],
		})),
		{
			value: ISSUE_NOTIFICATION_GENERAL_CATEGORY,
			label: 'General',
			description: 'Issues not linked to any record.',
		},
	].sort((a, b) => a.label.localeCompare(b.label));
